import React, { useEffect, useState, useCallback, useRef } from "react";
import { grantAdReward } from "@/lib/ads";
import { Button } from "@/components/ui/button";
import { Sparkles, Play, X, Check } from "lucide-react";

const MOCK_AD_DURATION = 5; // seconds — fallback when AdSense not ready/no-fill

/**
 * Full-screen rewarded-ad modal — real Google AdSense H5 rewarded via the
 * Ad Placement API (adBreak), with an automatic 5-second mock fallback when
 * AdSense isn't ready, ad-block is active, or no ad fills the slot.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - onRewarded (result) — called after backend confirms the reward
 *  - kind ("palm"|"chat"|"generic")
 *  - title (string, optional)
 *  - description (string, optional)
 */
export default function RewardedAdModal({ open, onClose, onRewarded, kind = "generic", title, description }) {
  const [phase, setPhase] = useState("intro"); // intro | playing | done | claiming | error
  const [remaining, setRemaining] = useState(MOCK_AD_DURATION);
  const [error, setError] = useState("");
  const [source, setSource] = useState("mock"); // 'adsense' | 'mock'
  const showAdFnRef = useRef(null);
  const rewardEarnedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setPhase("intro");
      setRemaining(MOCK_AD_DURATION);
      setError("");
      setSource("mock");
      showAdFnRef.current = null;
      rewardEarnedRef.current = false;
    }
  }, [open]);

  // Mock countdown — used only when AdSense doesn't fill.
  useEffect(() => {
    if (phase !== "playing" || source !== "mock") return;
    if (remaining <= 0) { setPhase("done"); return; }
    const t = setTimeout(() => setRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, remaining, source]);

  const claimReward = useCallback(async () => {
    setPhase("claiming");
    try {
      const result = await grantAdReward(kind);
      onRewarded?.(result);
      onClose?.();
    } catch (e) {
      setPhase("error");
      setError(e?.response?.data?.detail || e.message || "Reward failed");
    }
  }, [kind, onRewarded, onClose]);

  // Try to request a real AdSense rewarded ad. Falls back to mock silently.
  const requestAd = useCallback(() => {
    const adBreak = typeof window !== "undefined" ? window.adBreak : null;
    if (!adBreak) {
      // No AdSense at all → straight to mock playing state
      setSource("mock");
      setPhase("playing");
      return;
    }

    let receivedCallback = false;
    let requestTimer = setTimeout(() => {
      // AdSense never called back — fall back to mock
      if (!receivedCallback) {
        setSource("mock");
        setPhase("playing");
      }
    }, 3000);

    try {
      adBreak({
        type: "reward",
        name: `cosmic-unlock-${kind}`,
        beforeReward: (showAdFn) => {
          receivedCallback = true;
          clearTimeout(requestTimer);
          showAdFnRef.current = showAdFn;
          setSource("adsense");
          // AdSense placement is available — trigger it immediately (still
          // within the direct user-gesture chain, since requestAd was called
          // from the "Play Ad" click).
          try { showAdFn(); } catch (_) { /* noop */ }
        },
        beforeAd: () => {
          setPhase("playing");
        },
        adViewed: () => {
          // User completed the rewarded ad — mark earned. Actual reward call
          // happens on Claim (adDismissed / adBreakDone) so we can still show
          // the standard success screen.
          rewardEarnedRef.current = true;
        },
        adDismissed: () => {
          if (rewardEarnedRef.current) {
            setPhase("done");
          } else {
            // User dismissed before completion — no reward, close politely.
            setPhase("intro");
            setError("Ad closed early — no reward granted.");
          }
        },
        afterAd: () => { /* container hidden — no-op */ },
        adBreakDone: (info) => {
          receivedCallback = true;
          clearTimeout(requestTimer);
          // If beforeReward never fired we didn't have an ad → mock fallback.
          if (!showAdFnRef.current && phase === "intro") {
            setSource("mock");
            setPhase("playing");
          }
        },
      });
    } catch (e) {
      // Something crashed inside AdSense → mock fallback
      clearTimeout(requestTimer);
      setSource("mock");
      setPhase("playing");
    }
  }, [kind, phase]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4"
      data-testid="rewarded-ad-modal"
    >
      <div className="relative w-full max-w-md rounded-3xl border border-amber-500/30 bg-slate-900 p-8 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-slate-500 hover:text-slate-200"
          data-testid="rewarded-ad-close"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {phase === "intro" && (
          <div className="text-center">
            <Sparkles className="mx-auto mb-4 h-10 w-10 text-amber-400" />
            <h2 className="font-serif text-2xl text-slate-50">{title || "Watch a short ad"}</h2>
            <p className="mt-2 text-sm text-slate-400">
              {description || (
                kind === "chat"
                  ? "Play a short video to unlock 10 more minutes of AI Astro Talk."
                  : kind === "vastu"
                    ? "Play a short video to unlock 2 more Vastu analyses."
                    : "Play a short video to unlock 3 more premium readings — palm, kundli matching, lo-shu grid, dasha and more."
              )}
            </p>
            {error && (
              <p className="mt-3 text-xs text-rose-400" data-testid="rewarded-ad-inline-error">{error}</p>
            )}
            <Button
              onClick={requestAd}
              data-testid="rewarded-ad-play"
              className="mt-6 w-full rounded-full bg-amber-500 py-6 text-slate-950 hover:bg-amber-600"
            >
              <Play className="mr-2 h-4 w-4" /> Watch Ad
            </Button>
            <p className="mt-3 text-xs text-slate-600">Sponsored by Google AdSense — a short video will play.</p>
          </div>
        )}

        {phase === "playing" && source === "mock" && (
          <div className="text-center" data-testid="rewarded-ad-playing">
            <div className="relative flex h-56 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-900 via-slate-900 to-amber-900">
              <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: "radial-gradient(circle at 20% 30%, #FBBF24 0.5px, transparent 1px), radial-gradient(circle at 80% 70%, #A78BFA 0.5px, transparent 1px)",
                backgroundSize: "24px 24px, 32px 32px",
              }} />
              <div className="relative text-center">
                <div className="text-xs uppercase tracking-widest text-amber-300">House Ad</div>
                <div className="mt-2 font-serif text-4xl text-slate-50">Cosomic Sage</div>
                <div className="mt-1 text-sm text-slate-300">Vedic wisdom meets AI</div>
              </div>
              <div className="absolute bottom-3 right-3 rounded-full bg-slate-950/80 px-3 py-1 text-xs font-bold text-amber-300" data-testid="rewarded-ad-countdown">
                {remaining}s
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-500">Ad network not filling right now — showing our house ad.</p>
          </div>
        )}

        {phase === "playing" && source === "adsense" && (
          <div className="text-center" data-testid="rewarded-ad-playing-adsense">
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
            <p className="text-sm text-slate-400">A short sponsored video is playing...</p>
            <p className="mt-1 text-xs text-slate-600">Please complete it to unlock your reward.</p>
          </div>
        )}

        {phase === "done" && (
          <div className="text-center" data-testid="rewarded-ad-done">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
              <Check className="h-7 w-7 text-emerald-400" />
            </div>
            <h2 className="font-serif text-2xl text-slate-50">Reward Ready</h2>
            <p className="mt-2 text-sm text-slate-400">Claim your unlock and continue exploring.</p>
            <Button
              onClick={claimReward}
              data-testid="rewarded-ad-claim"
              className="mt-6 w-full rounded-full bg-amber-500 py-6 text-slate-950 hover:bg-amber-600"
            >
              Claim Reward
            </Button>
          </div>
        )}

        {phase === "claiming" && (
          <div className="text-center">
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
            <p className="text-sm text-slate-400">Unlocking...</p>
          </div>
        )}

        {phase === "error" && (
          <div className="text-center" data-testid="rewarded-ad-error">
            <p className="text-sm text-rose-400">{typeof error === "string" ? error : "Reward failed."}</p>
            <Button onClick={onClose} className="mt-4 rounded-full">Close</Button>
          </div>
        )}
      </div>
    </div>
  );
}
